import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPutJson } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import Empty from "../../components/design/Empty";
import { SkeletonRows } from "../../components/loading";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { MdAdd, MdContentCopy, MdDeleteOutline, MdEdit } from "react-icons/md";

/**
 * 角色與權限矩陣 — 版面對應 DESIGN 稿 L384-L398。
 *
 * 資料改走 V016 之後的真實權限矩陣：
 *   GET  /Auth/Role/Matrix?roleId    → 11 模組 × 4 動作 + 每模組資料範圍
 *   PUT  /Auth/Role/Permission       → 切換單一格
 *   PUT  /Auth/Role/Scope            → 設定資料範圍
 *
 * 之所以「一格一次請求」而不是整張表一起送：舊的 PUT /Auth/Role 會先刪光 role_permission
 * 再依傳入的 id 重建，只要前端狀態有一點不同步就會把整個角色的權限清掉。
 * 單格 upsert 沒有這個風險，也和設計稿「勾一下就 toast」的互動一致。
 */

/* 設計稿 L1172：資料範圍選項（順序即 scope 值 0/1/2） */
const SCOPE_OPTS = ["僅自己", "所屬專案", "全部"];

/* 左右兩欄的卡片頭 */
const PANEL_HEAD = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
  flexWrap: "wrap",
  px: 2.5,
  py: 2,
  borderBottom: 1,
  borderColor: "divider",
};

function App(props) {
  const { toast, confirmDelete } = useApp();
  const [selId, setSelId] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    props.GET_RoleList(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { roleList } = props;

  const roles = useMemo(() => {
    const arr = Array.isArray(roleList) ? roleList : _.get(roleList, "data_list", []) || [];
    return arr.map((r) => ({
      id: _.get(r, "id", _.get(r, "Id", null)),
      name: _.get(r, "name", _.get(r, "Name", "")) || "",
      desc: _.get(r, "note", _.get(r, "Note", "")) || "",
      perms: _.get(r, "permission_count", _.get(r, "permissionCount", 0)) || 0,
    }));
  }, [roleList]);

  /* 角色清單載入後預選第一個 */
  useEffect(() => {
    if (roles.length && (selId == null || !roles.some((r) => r.id === selId))) {
      setSelId(roles[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  const sel = useMemo(
    () => roles.find((r) => r.id === selId) || { id: null, name: "", desc: "", perms: 0 },
    [roles, selId]
  );

  const loadMatrix = useCallback(async (roleId) => {
    if (roleId == null) return;
    setLoadingMatrix(true);
    try {
      const d = await apiGet("/Auth/Role/Matrix", { roleId });
      setMatrix(d || null);
    } catch (e) {
      setMatrix(null);
    } finally {
      setLoadingMatrix(false);
    }
  }, []);

  useEffect(() => {
    loadMatrix(selId);
  }, [selId, loadMatrix]);

  const modules = useMemo(() => _.get(matrix, "modules", []) || [], [matrix]);
  const actions = useMemo(
    () =>
      _.get(matrix, "actions", []) || [
        { key: "view", name: "檢視" },
        { key: "create", name: "新增" },
        { key: "edit", name: "編輯" },
        { key: "delete", name: "刪除" },
      ],
    [matrix]
  );

  /**
   * 切換單一格。先樂觀更新畫面再送出，失敗時整張表重抓校正——
   * 收回「檢視」時後端會連帶清掉該模組的新增/編輯/刪除，樂觀更新算不出來，所以成功後也重抓。
   */
  const togglePerm = async (moduleGroup, action, current) => {
    if (sel.id == null) return;

    setMatrix((m) => ({
      ...m,
      modules: (m?.modules || []).map((mod) =>
        mod.module_group !== moduleGroup
          ? mod
          : {
              ...mod,
              cells: mod.cells.map((c) => (c.action === action ? { ...c, granted: !current } : c)),
            }
      ),
    }));

    try {
      await apiPutJson("/Auth/Role/Permission", {
        roleId: sel.id,
        moduleGroup,
        action,
        granted: !current,
      });
      toast("已更新權限");
      await loadMatrix(sel.id);
      props.GET_RoleList(null, null);
    } catch (e) {
      toast(apiErrorMessage(e, "權限更新失敗"), "error");
      await loadMatrix(sel.id);
    }
  };

  const setScope = async (moduleGroup, scope) => {
    if (sel.id == null) return;
    setMatrix((m) => ({
      ...m,
      modules: (m?.modules || []).map((mod) =>
        mod.module_group === moduleGroup ? { ...mod, scope } : mod
      ),
    }));
    try {
      await apiPutJson("/Auth/Role/Scope", { roleId: sel.id, moduleGroup, scope });
      toast("已更新資料範圍");
    } catch (e) {
      toast(apiErrorMessage(e, "資料範圍更新失敗"), "error");
      await loadMatrix(sel.id);
    }
  };

  /** 目前畫面上勾起來的權限 → permission_id 陣列（複製角色用） */
  const grantedIds = () =>
    modules.flatMap((m) =>
      m.cells.filter((c) => c.granted && c.permission_id != null).map((c) => c.permission_id)
    );

  const copyRole = () => {
    if (sel.id == null) return;
    /*
     * 複製角色沿用舊的 POST /Auth/Role；矩陣權限用目前畫面上勾起來的格子換算成 permission_id。
     * 矩陣算不出 project_list / task_list / document 這類不在矩陣上的子模組列，
     * 由後端 ExpandViewPermissions 依 module_group 補齊，複製出來的角色才跟原本的一樣能用。
     */
    const ids = grantedIds();
    props.POST_Role(
      { Name: `${sel.name} 複製`, Note: sel.desc, PermissionIds: ids },
      () => {
        props.GET_RoleList(null, null);
        toast("已複製角色");
      },
      null
    );
  };

  /**
   * 重新命名角色。
   *
   * 刻意不送 PermissionIds：後端 PutRole 收到 null 就完全不動 role_permission。
   *
   * 以前是「把目前矩陣上勾起來的格子一起送回去」，但矩陣只涵蓋 9 模組 × 4 動作，
   * 算不出 project_list / task_list / document / role / admin 這類不在矩陣上的子模組列 ——
   * 於是改一次名字就把那些權限靜默清光，而且矩陣上完全看不出來少了什麼。
   *
   * 用 window.prompt 而不是自製對話框：這是一個單欄位輸入，為它做一個 modal
   * 不划算，而全站也還沒有共用的「輸入框對話框」元件。
   */
  const renameRole = () => {
    if (sel.id == null) return;
    const next = window.prompt("角色名稱", sel.name);
    if (next === null) return;
    const name = String(next).trim();
    if (!name) { toast("角色名稱不可為空", "error"); return; }
    if (name === sel.name) return;
    props.PUT_Role(
      { RoleId: sel.id, Name: name, Note: sel.desc },
      () => { props.GET_RoleList(null, null); toast("已更新角色名稱"); },
      null
    );
  };

  /**
   * 刪除角色。
   *
   * 後端 DeleteRole 只清 role_permission 再刪 role —— admin_role 是
   * ON DELETE CASCADE，所以掛在這個角色底下的帳號會直接失去這組權限。
   * 這件事一定要在對話框裡講，否則刪完才發現一票人進不了系統。
   */
  const removeRole = () => {
    if (sel.id == null) return;
    confirmDelete({
      title: "刪除角色？",
      subtitle: "此操作無法復原",
      body:
        `確定要刪除角色「${sel.name}」嗎？\n` +
        `屬於此角色的帳號將失去這組權限；若此為該使用者唯一的角色，登入後將幾乎無法檢視任何內容。\n` +
        `請先確認已無帳號使用此角色，或先將相關帳號改為其他角色。`,
      onConfirm: () =>
        props.DELETE_Role(
          { RoleId: sel.id },
          () => { setSelId(null); props.GET_RoleList(null, null); toast("已刪除角色"); },
          null
        ),
    });
  };

  const submitAdd = () => {
    const name = draft.trim();
    if (!name) {
      toast("請輸入角色名稱", "error");
      return;
    }
    props.POST_Role({ Name: name, Note: "", PermissionIds: [] }, () => {
      setDraft("");
      setAdding(false);
      props.GET_RoleList(null, null);
      toast("已新增角色");
    }, null);
  };

  return (
    <Box className="pg" sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
      {/* 左欄：角色清單 */}
      <Card sx={{ flex: 1, minWidth: 250 }}>
        <Box sx={PANEL_HEAD}>
          <Typography variant="h3" component="h2">
            角色
          </Typography>
          <Button variant="text" type="button" startIcon={<MdAdd />} onClick={() => setAdding((v) => !v)}>
            新增
          </Button>
        </Box>

        {adding && (
          <Box sx={{ display: "flex", gap: 1, px: 2.5, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitAdd();
              }}
              placeholder="角色名稱"
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Button variant="contained" type="button" onClick={submitAdd}>
              新增
            </Button>
          </Box>
        )}

        {roles.length === 0 && <Empty title="尚無角色" variant="plain" pad="40px 20px" />}

        <List disablePadding>
          {roles.map((r) => {
            const on = r.id === sel.id;
            return (
              <ListItemButton
                key={r.id}
                divider
                selected={on}
                onClick={() => setSelId(r.id)}
                sx={{
                  justifyContent: "space-between",
                  gap: 1.5,
                  px: 2.5,
                  py: 1.75,
                  ...(on ? { boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}` } : {}),
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1">{r.name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {r.desc}
                  </Typography>
                </Box>
                <Chip size="small" label={`${r.perms} 權限`} />
              </ListItemButton>
            );
          })}
        </List>
      </Card>

      {/* 右欄：權限矩陣 */}
      <Card sx={{ flex: 2, minWidth: 320 }}>
        <Box sx={PANEL_HEAD}>
          <div>
            <Typography variant="h3" component="h2">
              權限矩陣 · {sel.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              模組 × 動作（CRUD）與資料範圍
            </Typography>
          </div>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Button variant="outlined" type="button" startIcon={<MdContentCopy />} onClick={copyRole}>
              複製角色
            </Button>

            {/* 角色原本建得了卻改不了名字也刪不掉 —— PUT / DELETE 兩支端點都在，只是沒有入口 */}
            <Tooltip title="重新命名此角色">
              <IconButton type="button" onClick={renameRole} aria-label="重新命名此角色">
                <MdEdit />
              </IconButton>
            </Tooltip>
            <Tooltip title="刪除此角色">
              <IconButton type="button" onClick={removeRole} aria-label="刪除此角色" color="error">
                <MdDeleteOutline />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {modules.length === 0 ? (
          loadingMatrix ? (
            <SkeletonRows rows={6} sx={{ p: 2.5 }} />
          ) : (
            <Empty title="尚未取得權限矩陣" hint="請確認您具備「角色管理」權限" variant="plain" pad="48px 20px" />
          )
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>模組</TableCell>
                  {actions.map((a) => (
                    <TableCell key={a.key} align="center" sx={{ fontWeight: 700, color: "text.secondary" }}>
                      {a.name}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary" }}>資料範圍</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((mod) => (
                  <TableRow key={mod.module_group}>
                    <TableCell sx={{ fontWeight: 600 }}>{mod.module_name}</TableCell>
                    {actions.map((a) => {
                      const cell = (mod.cells || []).find((c) => c.action === a.key) || {};
                      const cur = !!cell.granted;
                      const usable = cell.available !== false;
                      return (
                        <TableCell key={a.key} align="center" sx={{ py: 0.5 }}>
                          {/* 停用的 Checkbox 收不到滑鼠事件，Tooltip 要掛在外層的 span 上 */}
                          <Tooltip title={usable ? "" : "此模組沒有對應的權限項目"}>
                            <span>
                              <Checkbox
                                checked={cur}
                                disabled={!usable}
                                onChange={() => togglePerm(mod.module_group, a.key, cur)}
                                slotProps={{ input: { "aria-label": `${mod.module_name}・${a.name}` } }}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ py: 0.5 }}>
                      <TextField
                        select
                        size="small"
                        value={String(mod.scope ?? 0)}
                        onChange={(e) => setScope(mod.module_group, Number(e.target.value))}
                        slotProps={{ select: { native: true }, htmlInput: { "aria-label": `${mod.module_name} 資料範圍` } }}
                      >
                        {SCOPE_OPTS.map((o, i) => (
                          <option key={o} value={String(i)}>
                            {o}
                          </option>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  roleList: _.get(state, "Role.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_RoleList: (callback, loading) => dispatch({ type: "GET_RoleList", callback, loading }),
  POST_Role: (payload, callback, loading) => dispatch({ type: "POST_Role", payload, callback, loading }),
  PUT_Role: (payload, callback, loading) => dispatch({ type: "PUT_Role", payload, callback, loading }),
  DELETE_Role: (payload, callback, loading) => dispatch({ type: "DELETE_Role", payload, callback, loading }),
});

App.propTypes = {
  roleList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_RoleList: PropTypes.func,
  POST_Role: PropTypes.func,
  PUT_Role: PropTypes.func,
  DELETE_Role: PropTypes.func,
};

App.defaultProps = {
  roleList: [],
  GET_RoleList: () => {},
  POST_Role: () => {},
  PUT_Role: () => {},
  DELETE_Role: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
